/**
 * WidgetBar — recharts BarChart. Expects data shaped as
 * `[{ bucket: string, value: number }, ...]` or similar. The executor's
 * bar-widget path returns exactly this shape (see report-executor.js).
 */

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import type { BarWidget } from '../types'

interface WidgetBarProps {
  widget: BarWidget
  data?: unknown
  error?: string
}

interface BarDatum {
  bucket: string
  value: number
}

function normalize(data: unknown): BarDatum[] {
  if (!Array.isArray(data)) return []
  return data
    .map((row) => {
      if (!row || typeof row !== 'object') return null
      const obj = row as Record<string, unknown>
      const bucket = String(obj.bucket ?? obj.label ?? obj.name ?? '')
      const valueRaw = obj.value ?? obj.total ?? obj.count
      const value = typeof valueRaw === 'number' ? valueRaw : Number(valueRaw)
      if (!bucket || !Number.isFinite(value)) return null
      return { bucket, value }
    })
    .filter((x): x is BarDatum => x !== null)
}

export function WidgetBar({ widget, data, error }: WidgetBarProps) {
  const rows = normalize(data)
  return (
    <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {widget.title}
      </div>
      {error || rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-xs text-slate-400">
          {error ?? 'no data'}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar dataKey="value" fill="#0070ab" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
